import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import { resetPassword } from "../../api/usermanager";
import { toast } from "react-toastify";

type PasswordField = "password" | "confirmPassword";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const route = all_routes;

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
  });

  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    specialChar: false,
  });

  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: "",
  });

  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false,
  });

  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const styleId = "animated-badge-keyframes";
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.innerText = `
        @keyframes shine {
          0% {
            transform: skewX(-20deg) translateX(-150%);
          }
          50% {
             transform: skewX(-20deg) translateX(150%);
          }
          100% {
            transform: skewX(-20deg) translateX(150%);
          }
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenFromURL = searchParams.get("token");
    if (tokenFromURL) {
      setToken(tokenFromURL);
    } else {
      toast.error("Invalid or missing token.");
      navigate(route.login);
    }
  }, [location.search, navigate, route.login]);

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validatePassword = (value: string) => {
    const validations = {
      length: value.length >= 8,
      uppercase: /[A-Z]/.test(value),
      lowercase: /[a-z]/.test(value),
      digit: /[0-9]/.test(value),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(value),
    };

    setPasswordValidations(validations);

    // Basic required check
    if (!value.trim()) {
      return "Password is required";
    }

    // Additional strict checks
    if (value.length > 128) {
      return "Password must not exceed 128 characters";
    }

    if (/\s{2,}/.test(value)) {
      return "Password cannot contain consecutive spaces";
    }

    if (/(.)\1{5,}/.test(value)) {
      return "Password contains too many repeated characters";
    }

    // All criteria must be met
    if (!Object.values(validations).every(Boolean)) {
      return ""; // We'll show checklist instead of generic message
    }

    return "";
  };

  const validateConfirmPassword = (confirm: string, original: string) => {
    if (!confirm.trim()) {
      return "Confirm password is required";
    }
    if (confirm !== original) {
      return "Passwords do not match";
    }
    return "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "password") {
      const error = validatePassword(value);
      setErrors((prev) => ({ ...prev, password: error }));
      // Update validations for checklist
      setPasswordValidations({
        length: value.length >= 8,
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        digit: /[0-9]/.test(value),
        specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(value),
      });
    }

    if (name === "confirmPassword") {
      const error = validateConfirmPassword(value, formData.password);
      setErrors((prev) => ({ ...prev, confirmPassword: error }));
    }
  };

  const handleBlur = (field: "password" | "confirmPassword") => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    if (field === "password") {
      const error = validatePassword(formData.password);
      setErrors((prev) => ({ ...prev, password: error }));
    }
    if (field === "confirmPassword") {
      const error = validateConfirmPassword(
        formData.confirmPassword,
        formData.password
      );
      setErrors((prev) => ({ ...prev, confirmPassword: error }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTouched({ password: true, confirmPassword: true });

    const passwordError = validatePassword(formData.password);
    const confirmError = validateConfirmPassword(
      formData.confirmPassword,
      formData.password
    );

    setErrors({
      password: passwordError,
      confirmPassword: confirmError,
    });

    if (passwordError || confirmError) {
      if (passwordError) toast.error(passwordError);
      if (confirmError) toast.error(confirmError);
      return;
    }

    if (!Object.values(passwordValidations).every(Boolean)) {
      toast.error("Password does not meet all requirements.");
      return;
    }

    try {
      setLoading(true);
      await resetPassword(token, formData.password);
      toast.success("Password reset successfully!");
      navigate(route.login);
    } catch (err) {
      toast.error("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-container">
      {/* LEFT SIDE – STATIC IMAGE */}
      <div className="login-left-image">
        <ImageWithBasePath
          src="assets/img/authentication/forgot-img.png"
          className="img-fluid auth-static-img"
          alt="Password reset illustration"
        />
      </div>

      {/* RIGHT SIDE – CARD */}
      <div className="login-right-card">
        <div className="login-wrapper" role="main">
          <form onSubmit={handleSubmit} aria-live="polite">
            {/* LOGO */}
            <div className="text-center mb-4">
              <div className="logo-wrapper">
                <ImageWithBasePath
                src="assets/img/logo.png"
                  className="img-fluid"
                  alt="IATT CRMS Logo"
                />
              </div>
            </div>

            {/* TITLE */}
            <div className="text-center mb-4">
              <h4 className="fw-semibold" id="reset-title">
                Reset Password
              </h4>
              <p className="text-muted" id="reset-desc">
                Enter and confirm your new password
              </p>
            </div>

            {/* NEW PASSWORD */}
            <div className="mb-3">
              <label htmlFor="password" className="form-label">
                New Password <span className="text-danger">*</span>
              </label>
              <div className="input-with-icon">
                <i
                  className={`bi ${
                    passwordVisibility.password ? "bi-eye" : "bi-eye-slash"
                  }`}
                  onClick={() => togglePasswordVisibility("password")}
                  style={{ cursor: "pointer", fontSize: "18px", opacity: 0.8 }}
                ></i>
                <input
                  id="password"
                  type={passwordVisibility.password ? "text" : "password"}
                  className={`form-control ${
                    errors.password ||
                    (touched.password &&
                      !Object.values(passwordValidations).every(Boolean))
                      ? "is-invalid"
                      : ""
                  }`}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={() => handleBlur("password")}
                  placeholder="Enter new password"
                  aria-label="New password"
                  aria-required="true"
                />
              </div>

              {/* Password Requirements Checklist */}
              <div className="mt-2 small">
                <ul style={{ paddingLeft: "1.4rem", margin: 0 }}>
                  <li
                    style={{
                      color: passwordValidations.length ? "#28a745" : "#dc3545",
                    }}
                  >
                    Minimum 8 characters
                  </li>
                  <li
                    style={{
                      color: passwordValidations.uppercase
                        ? "#28a745"
                        : "#dc3545",
                    }}
                  >
                    At least one uppercase letter
                  </li>
                  <li
                    style={{
                      color: passwordValidations.lowercase
                        ? "#28a745"
                        : "#dc3545",
                    }}
                  >
                    At least one lowercase letter
                  </li>
                  <li
                    style={{
                      color: passwordValidations.digit ? "#28a745" : "#dc3545",
                    }}
                  >
                    At least one digit
                  </li>
                  <li
                    style={{
                      color: passwordValidations.specialChar
                        ? "#28a745"
                        : "#dc3545",
                    }}
                  >
                    At least one special character (!@#$%^&* etc.)
                  </li>
                </ul>
              </div>

              {errors.password && touched.password && (
                <small className="text-danger">{errors.password}</small>
              )}
            </div>

            {/* CONFIRM PASSWORD */}
            <div className="mb-3">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password <span className="text-danger">*</span>
              </label>
              <div className="input-with-icon">
                <i
                  className={`bi ${
                    passwordVisibility.confirmPassword
                      ? "bi-eye"
                      : "bi-eye-slash"
                  }`}
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                  style={{ cursor: "pointer", fontSize: "18px", opacity: 0.8 }}
                ></i>
                <input
                  id="confirmPassword"
                  type={
                    passwordVisibility.confirmPassword ? "text" : "password"
                  }
                  className={`form-control ${
                    errors.confirmPassword ? "is-invalid" : ""
                  }`}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={() => handleBlur("confirmPassword")}
                  placeholder="Re-enter new password"
                  aria-label="Confirm password"
                  aria-required="true"
                />
              </div>
              {errors.confirmPassword && touched.confirmPassword && (
                <small className="text-danger">{errors.confirmPassword}</small>
              )}
            </div>

            {/* SUBMIT */}
            <button
              type="submit"
              className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
              disabled={loading}
              aria-busy={loading}
              style={{
                position: "relative",
                overflow: "hidden",
              }}
            >
              {loading && (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                  style={{ position: "relative", zIndex: 2 }}
                />
              )}
              <span className="visually-hidden" aria-live="assertive" style={{ position: "relative", zIndex: 2 }}>
                {loading ? "Changing password" : ""}
              </span>
              <span style={{ position: "relative", zIndex: 2 }}>
                {loading ? "Changing..." : "Change Password"}
              </span>
              <span
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: "linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)",
                  transform: "skewX(-20deg) translateX(-150%)",
                  animation: "shine 3s infinite",
                }}
              />
            </button>

            {/* BACK TO LOGIN */}
            <div className="text-center mt-4">
              <h6>
                Return to{" "}
                <Link to={route.login} className="text-primary fw-medium">
                  Login
                </Link>
              </h6>
            </div>

            {/* FOOTER */}
            <div className="text-center mt-2">
              <p className="footer-text">© 2025 - IATT | CRMS</p>
            </div>
          </form>
        </div>
      </div>

      {/* STYLES - Reused from ForgotPassword with left/right swap */}
      <style>{`
        .login-page-container {
          display: flex;
          height: 100vh;
          width: 100%;
          overflow: hidden;
          flex-direction: row-reverse; /* This swaps sides: card right, image left */
        }

        .login-right-card {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
          background: linear-gradient(
            135deg,
            #ffeaea 0%,
            #fff5f5 50%,
            #ffdede 100%
          );
        }

        .login-wrapper {
          width: 100%;
          max-width: 420px;
          background: #ffffff;
          border-radius: 18px;
          padding: 32px;
          box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
          animation: fadeInUp 0.8s ease forwards;
        }

        .logo-wrapper img {
          width: 120px;
        }

        .login-left-image {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f8f9fa;
          animation: fadeInDelayed 1s ease forwards;
        }

        .auth-static-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 40px;
        }

        .footer-text {
          color: #aaa;
          font-size: 13px;
        }

        .form-control:focus,
        .btn:focus {
          outline: none;
          border-color: #e41f07;
          box-shadow: 0 0 0 3px rgba(228, 31, 7, 0.25);
        }

        @media (max-width: 768px) {
          .login-page-container {
            flex-direction: column;
          }
          .login-left-image {
            display: none;
          }
          .login-right-card {
            padding: 20px;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes fadeInDelayed {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .input-with-icon {
          position: relative;
        }

        .input-with-icon i {
          position: absolute;
          top: 50%;
          left: 12px;
          transform: translateY(-50%);
          font-size: 18px;
          opacity: 0.8;
          z-index: 2;
        }

        .input-with-icon .form-control {
          padding-left: 40px !important;
          height: 45px;
          border-radius: 8px;
        }

        .input-with-icon .form-control:focus {
          border-color: #e41f07;
          box-shadow: 0 0 0 3px rgba(228, 31, 7, 0.18);
          outline: none;
        }
      `}</style>
    </div>
  );
};

export default ResetPassword;
